import React from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Position } from '@xyflow/react';
import { connStyle } from '@/lib/connections';
import { useStore } from '@/state/store';

function PortLabel({ ax, ay, bx, by, text, horizontal, mirrored }) {
  const dx = bx - ax;
  const dy = by - ay;
  // Sit the chip a short fixed distance ALONG the cable from the port and centre
  // it ON the connection line. For vertical runs the chip (and its text) is
  // rotated 90° so it always reads in the direction of the cable and stays clear
  // of neighbouring parallel lanes.
  const along = 22;
  const px = horizontal ? ax + (dx >= 0 ? along : -along) : ax;
  const py = horizontal ? ay : ay + (dy >= 0 ? along : -along);
  const rot = horizontal ? 0 : -90;
  return (
    <div
      style={{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${px}px, ${py}px) rotate(${rot}deg)`,
        pointerEvents: 'none',
        opacity: mirrored ? 0.5 : 1,
      }}
      className="whitespace-nowrap rounded bg-white/90 px-1 py-[1px] font-mono text-[9px] leading-none text-[#2A3138] ring-1 ring-[#1b232c]/15"
    >
      {text}
    </div>
  );
}

// Count chip shown at a bundled (busy) device-side anchor: instead of the
// individual port labels, one accent pill states how many cables converge here.
function CountLabel({ ax, ay, bx, by, n, horizontal, mirrored }) {
  const dx = bx - ax;
  const dy = by - ay;
  const along = 26;
  const px = horizontal ? ax + (dx >= 0 ? along : -along) : ax;
  const py = horizontal ? ay : ay + (dy >= 0 ? along : -along);
  const rot = horizontal ? 0 : -90;
  return (
    <div
      style={{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${px}px, ${py}px) rotate(${rot}deg)`,
        pointerEvents: 'none',
        opacity: mirrored ? 0.5 : 1,
      }}
      className="whitespace-nowrap rounded-full border border-[#0B5FFF]/45 bg-white px-1.5 py-[1px] font-mono text-[10px] font-bold leading-none text-[#0B5FFF] shadow-sm"
      data-testid="bundle-count-label"
    >
      {n}
    </div>
  );
}

export function ConnectionEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}) {
  const showPortLabels = useStore((s) => s.showPortLabels);
  // Parallel connections between the same two devices are fanned out so they read
  // as distinct cables instead of overlapping on a shared orthogonal corridor.
  // Two complementary techniques, both keyed off the edge's index within its
  // device-pair group:
  //  - center-lane stagger: shifts the long connecting (mid) segment onto its own
  //    track (centerX for horizontal exits / centerY for vertical exits). This is
  //    what stops parallel cables overlapping on long runs.
  //  - endpoint fan-out: shifts the endpoints perpendicular to the exit direction
  //    so cables that share ONE handle (icon-to-icon LAGs) also separate near the
  //    port. Skipped in 'lane' mode (detail pages) where every cable already ends
  //    at a distinct port and shifting would detach it from its handle.
  const idx = data?.parallelIndex ?? 0;
  const count = data?.parallelCount ?? 1;
  const laneOnly = data?.routeSpread === 'lane';
  const isHorizontal = sourcePosition === Position.Left || sourcePosition === Position.Right;
  // Side-bundle collapse (standard pages only): when a device side carries more
  // than the separate-display limit, all its cables dock on the side's single
  // anchor (no fan-out) so they read as one line, and a count label is shown.
  const srcCollapsed = !!data?.bundleSourceCollapsed;
  const tgtCollapsed = !!data?.bundleTargetCollapsed;
  const spread = count > 1 ? idx - (count - 1) / 2 : 0;
  const gap = 14;
  const off = spread * gap;
  const srcOff = srcCollapsed ? 0 : off;
  const tgtOff = tgtCollapsed ? 0 : off;
  // The connecting corridor keeps its own lane only when NEITHER end is bundled.
  const midOff = srcCollapsed || tgtCollapsed ? 0 : off;

  let sX = sourceX;
  let sY = sourceY;
  let tX = targetX;
  let tY = targetY;
  const stepOpts = { borderRadius: 6 };
  if (count > 1) {
    // Stagger the connecting (mid) segment to its own lane, but CLAMP the
    // stagger so the staggered centre never leaves the corridor between the
    // stub-gapped source and target. Without the clamp a large stagger on a
    // SHORT run pushes the mid-segment past an endpoint and getSmoothStepPath
    // bends the cable backwards — the S-shaped "wrong direction" kink seen when
    // two devices sit close together on a detail/panel page with a slight
    // offset. NOTE: getSmoothStepPath only honours centerX/centerY for
    // OPPOSITE handle positions (Bottom->Top, Right->Left); same-side pairs
    // ignore it, so this clamp is a no-op there and safe to apply always.
    const STUB = 20; // matches getSmoothStepPath's default `offset`
    if (isHorizontal) {
      const sg = sourceX + (sourcePosition === Position.Left ? -STUB : STUB);
      const tg = targetX + (targetPosition === Position.Left ? -STUB : STUB);
      const lo = Math.min(sg, tg);
      const hi = Math.max(sg, tg);
      stepOpts.centerX = Math.max(lo, Math.min(hi, (sourceX + targetX) / 2 + midOff));
    } else {
      const sg = sourceY + (sourcePosition === Position.Top ? -STUB : STUB);
      const tg = targetY + (targetPosition === Position.Top ? -STUB : STUB);
      const lo = Math.min(sg, tg);
      const hi = Math.max(sg, tg);
      stepOpts.centerY = Math.max(lo, Math.min(hi, (sourceY + targetY) / 2 + midOff));
    }
    // Fan out shared-handle endpoints (unless lane-only / detail pages). A
    // collapsed end uses offset 0 so its cables converge on one anchor.
    if (!laneOnly) {
      sX = sourceX + (isHorizontal ? 0 : srcOff);
      sY = sourceY + (isHorizontal ? srcOff : 0);
      tX = targetX + (isHorizontal ? 0 : tgtOff);
      tY = targetY + (isHorizontal ? tgtOff : 0);
    }
  }

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: sX,
    sourceY: sY,
    targetX: tX,
    targetY: tY,
    sourcePosition,
    targetPosition,
    ...stepOpts,
  });
  const style = connStyle(data?.connType, selected);
  const mirrored = !!data?.mirrored;
  const edgeStyle = mirrored ? { ...style, opacity: 0.4 } : style;
  // Show the LAG badge on EVERY member connection (so multiple LAGs between the
  // same pair are each clearly labelled) and place it ON that line. Labels are
  // rotated to run along the cable for vertical runs, and staggered a little
  // along the cable so neighbouring parallel labels never overlap.
  const showLag = !!data?.lagName;
  const lagRot = isHorizontal ? 0 : -90;
  const alongStagger = (idx - (count - 1) / 2) * 26;
  const lagLabelX = isHorizontal ? labelX + alongStagger : labelX;
  const lagLabelY = isHorizontal ? labelY : labelY + alongStagger;
  return (
    <>
      <BaseEdge id={id} path={path} style={edgeStyle} />
      <EdgeLabelRenderer>
        {showPortLabels && data?.sourcePort && !srcCollapsed ? (
          <PortLabel ax={sX} ay={sY} bx={tX} by={tY} text={data.sourcePort} horizontal={isHorizontal} mirrored={mirrored} />
        ) : null}
        {showPortLabels && data?.targetPort && !tgtCollapsed ? (
          <PortLabel ax={tX} ay={tY} bx={sX} by={sY} text={data.targetPort} horizontal={isHorizontal} mirrored={mirrored} />
        ) : null}
        {data?.bundleSourceLabelOwner ? (
          <CountLabel ax={sX} ay={sY} bx={tX} by={tY} n={data.bundleSourceCount} horizontal={isHorizontal} mirrored={mirrored} />
        ) : null}
        {data?.bundleTargetLabelOwner ? (
          <CountLabel ax={tX} ay={tY} bx={sX} by={sY} n={data.bundleTargetCount} horizontal={isHorizontal} mirrored={mirrored} />
        ) : null}
        {showLag ? (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${lagLabelX}px, ${lagLabelY}px) rotate(${lagRot}deg)`,
              pointerEvents: 'none',
              borderColor: data.lagColor || '#0B5FFF',
              color: data.lagColor || '#0B5FFF',
            }}
            className="whitespace-nowrap rounded-full border bg-white/95 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide shadow-sm"
            data-testid={`lag-label-${id}`}
          >
            {`LAG · ${data.lagName}`}
          </div>
        ) : null}
      </EdgeLabelRenderer>
    </>
  );
}
